import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { siteBuilderAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Slider } from '../../components/ui/slider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import ImageUploader from '../../components/ImageUploader';
import { 
  FiSave, FiEye, FiImage, FiType, FiMenu, FiGrid, 
  FiRefreshCw, FiPlus, FiTrash2, FiEdit2, FiX,
  FiMonitor, FiSmartphone, FiExternalLink,
  FiChevronDown, FiChevronRight, FiSettings,
  FiLayers, FiMove, FiFile, FiFolder, FiStar, FiHelpCircle,
  FiArrowUp, FiArrowDown, FiVideo, FiMessageSquare, FiList
} from 'react-icons/fi';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const getApiUrl = () => { if (typeof window !== 'undefined') { const hostname = window.location.hostname; if (hostname.includes('justasmile-events.com') || hostname.includes('app.justasmile')) { return ''; } } return process.env.REACT_APP_BACKEND_URL || ''; }; const API_URL = getApiUrl();

// Section type labels
const SECTION_LABELS = {
  hero: 'En-tête Hero',
  hero_video: 'En-tête Vidéo',
  services_carousel: 'Carousel Services',
  stats: 'Statistiques',
  about: 'À propos',
  events_carousel: 'Carousel Événements',
  cta: 'Appel à l\'action',
  text_image: 'Texte + Image',
  gallery: 'Galerie',
  faq: 'FAQ',
  testimonials: 'Témoignages',
  contact_form: 'Formulaire Contact',
  features_grid: 'Grille de fonctionnalités',
};

export default function PageEditorPage() {
  const navigate = useNavigate();
  const { pageSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [site, setSite] = useState(null);
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [iframeKey, setIframeKey] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState({ 'Pages principales': true, 'Présentations produits': true });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const iframeRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);

  useEffect(() => {
    loadSite();
  }, []);

  useEffect(() => {
    if (pages.length > 0 && pageSlug) {
      const page = pages.find(p => p.slug === pageSlug);
      if (page) {
        setCurrentPage(JSON.parse(JSON.stringify(page)));
      }
    }
  }, [pageSlug, pages]);

  const loadSite = async () => {
    try {
      const response = await siteBuilderAPI.getMySite();
      const siteData = response.data;
      setSite(siteData);
      setPages(siteData.pages || []);
      
      if (!pageSlug && siteData.pages?.length > 0) {
        const homePage = siteData.pages.find(p => p.slug === 'accueil') || siteData.pages[0];
        navigate(`/dashboard/pages/${homePage.slug}`, { replace: true });
      }
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Use the dedicated page update endpoint
      await siteBuilderAPI.updatePage({
        page_slug: currentPage.slug,
        sections: currentPage.sections || []
      });
      
      // Update local state
      const updatedPages = pages.map(p => 
        p.slug === currentPage.slug ? currentPage : p
      );
      setSite({ ...site, pages: updatedPages });
      setPages(updatedPages);
      setHasUnsavedChanges(false);
      
      toast.success('Page enregistrée !');
      
      // Refresh preview after a short delay
      setTimeout(() => {
        setIframeKey(prev => prev + 1);
      }, 500);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Auto-save with debounce (saves after 2 seconds of inactivity)
  const triggerAutoSave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    autoSaveTimeoutRef.current = setTimeout(async () => {
      if (currentPage && hasUnsavedChanges) {
        try {
          await siteBuilderAPI.updatePage({
            page_slug: currentPage.slug,
            sections: currentPage.sections || []
          });
          setHasUnsavedChanges(false);
          // Silently refresh preview
          setIframeKey(prev => prev + 1);
        } catch (error) {
          console.error('Auto-save error:', error);
        }
      }
    }, 2000);
  }, [currentPage, hasUnsavedChanges]);

  // Update section content
  const updateSectionContent = (sectionIndex, field, value) => {
    if (!currentPage) return;
    const newSections = [...(currentPage.sections || [])];
    newSections[sectionIndex] = {
      ...newSections[sectionIndex],
      content: {
        ...newSections[sectionIndex].content,
        [field]: value
      }
    };
    setCurrentPage({ ...currentPage, sections: newSections });
    setHasUnsavedChanges(true);
    triggerAutoSave();
  };

  // Update page content (for presentation pages)
  const updatePageContent = (path, value) => {
    if (!currentPage) return;
    const newPage = { ...currentPage };
    const parts = path.split('.');
    let current = newPage;
    
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) current[parts[i]] = {};
      current[parts[i]] = { ...current[parts[i]] };
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = value;
    setCurrentPage(newPage);
    setHasUnsavedChanges(true);
    triggerAutoSave();
  };

  // Group pages
  const groupedPages = pages.reduce((acc, page) => {
    let group = 'Autres';
    if (['accueil', 'faq', 'contact', 'formules'].includes(page.slug)) {
      group = 'Pages principales';
    } else if (page.slug?.includes('presentation')) {
      group = 'Présentations produits';
    } else if (['photobooth', 'videobooth-360', 'livre-dor-video'].includes(page.slug)) {
      group = 'Pages produits';
    } else if (page.slug?.includes('tarif')) {
      group = 'Tarifs';
    }
    if (!acc[group]) acc[group] = [];
    acc[group].push(page);
    return acc;
  }, {});

  // Check page type
  const isPresentationPage = currentPage?.content && Object.keys(currentPage.content).length > 0;
  const hasSections = currentPage?.sections && currentPage.sections.length > 0;

  // Render a single section editor
  const renderSectionEditor = (section, index) => {
    const content = section.content || {};
    const sectionType = section.type || 'unknown';
    
    return (
      <AccordionItem key={index} value={`section-${index}`}>
        <AccordionTrigger className="px-4 hover:bg-slate-50">
          <div className="flex items-center gap-2 w-full">
            <Badge variant="outline" className="text-xs shrink-0">
              {SECTION_LABELS[sectionType] || sectionType}
            </Badge>
            {content.title && (
              <span className="text-sm text-slate-500 truncate">
                {content.title.substring(0, 30)}...
              </span>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-4">
          {/* Title */}
          {content.title !== undefined && (
            <div>
              <Label className="text-xs font-medium">Titre</Label>
              <Input
                value={content.title || ''}
                onChange={(e) => updateSectionContent(index, 'title', e.target.value)}
                className="mt-1"
              />
            </div>
          )}
          
          {/* Subtitle */}
          {content.subtitle !== undefined && (
            <div>
              <Label className="text-xs font-medium">Sous-titre</Label>
              <Textarea
                value={content.subtitle || ''}
                onChange={(e) => updateSectionContent(index, 'subtitle', e.target.value)}
                className="mt-1"
                rows={2}
              />
            </div>
          )}
          
          {/* Text / Description */}
          {(content.text !== undefined || content.description !== undefined) && (
            <div>
              <Label className="text-xs font-medium">Texte</Label>
              <Textarea
                value={content.text || content.description || ''}
                onChange={(e) => updateSectionContent(index, content.text !== undefined ? 'text' : 'description', e.target.value)}
                className="mt-1"
                rows={4}
              />
            </div>
          )}
          
          {/* Highlight Text */}
          {content.highlightText !== undefined && (
            <div>
              <Label className="text-xs font-medium">Texte mis en avant</Label>
              <Input
                value={content.highlightText || ''}
                onChange={(e) => updateSectionContent(index, 'highlightText', e.target.value)}
                className="mt-1"
              />
            </div>
          )}
          
          {/* Button Text */}
          {content.buttonText !== undefined && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-medium">Texte du bouton</Label>
                <Input
                  value={content.buttonText || ''}
                  onChange={(e) => updateSectionContent(index, 'buttonText', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">Lien du bouton</Label>
                <Input
                  value={content.buttonLink || ''}
                  onChange={(e) => updateSectionContent(index, 'buttonLink', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          )}
          
          {/* Image */}
          {(content.image !== undefined || content.backgroundImage !== undefined) && (
            <div>
              <Label className="text-xs font-medium">Image</Label>
              <div className="mt-1">
                <ImageUploader
                  value={content.image || content.backgroundImage || ''}
                  onChange={(url) => updateSectionContent(index, content.image !== undefined ? 'image' : 'backgroundImage', url)}
                />
              </div>
              {/* Image Position */}
              {(content.imagePositionX !== undefined || content.imagePositionY !== undefined) && (
                <div className="mt-2 space-y-2 p-2 bg-slate-50 rounded">
                  <Label className="text-xs text-slate-500">Position de l'image</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs w-6">X:</span>
                    <Slider
                      value={[content.imagePositionX || 50]}
                      onValueChange={(v) => updateSectionContent(index, 'imagePositionX', v[0])}
                      min={0} max={100} step={1}
                      className="flex-1"
                    />
                    <span className="text-xs w-8">{content.imagePositionX || 50}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs w-6">Y:</span>
                    <Slider
                      value={[content.imagePositionY || 50]}
                      onValueChange={(v) => updateSectionContent(index, 'imagePositionY', v[0])}
                      min={0} max={100} step={1}
                      className="flex-1"
                    />
                    <span className="text-xs w-8">{content.imagePositionY || 50}%</span>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Video URL */}
          {content.videoUrl !== undefined && (
            <div>
              <Label className="text-xs font-medium">URL de la vidéo</Label>
              <Input
                value={content.videoUrl || ''}
                onChange={(e) => updateSectionContent(index, 'videoUrl', e.target.value)}
                className="mt-1"
                placeholder="https://youtube.com/..."
              />
            </div>
          )}
          
          {/* Items (for carousels, FAQ, etc.) */}
          {content.items && Array.isArray(content.items) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium flex items-center gap-1">
                  <FiList className="h-3 w-3" />
                  Éléments ({content.items.length})
                </Label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const newItem = sectionType === 'faq' 
                      ? { question: '', answer: '' }
                      : sectionType.includes('carousel')
                      ? { title: '', subtitle: '', image: '', link: '' }
                      : { title: '', description: '', image: '' };
                    updateSectionContent(index, 'items', [...content.items, newItem]);
                  }}
                >
                  <FiPlus className="h-3 w-3" />
                </Button>
              </div>
              
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {content.items.map((item, itemIdx) => (
                  <div key={itemIdx} className="p-2 bg-slate-50 rounded border space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">
                        {sectionType === 'faq' ? `Question ${itemIdx + 1}` : `Élément ${itemIdx + 1}`}
                      </span>
                      <button
                        onClick={() => {
                          const newItems = content.items.filter((_, i) => i !== itemIdx);
                          updateSectionContent(index, 'items', newItems);
                        }}
                        className="text-red-500 p-1 hover:bg-red-50 rounded"
                      >
                        <FiTrash2 className="h-3 w-3" />
                      </button>
                    </div>
                    
                    {/* FAQ style items */}
                    {item.question !== undefined && (
                      <>
                        <Input
                          value={item.question || ''}
                          onChange={(e) => {
                            const newItems = [...content.items];
                            newItems[itemIdx] = { ...item, question: e.target.value };
                            updateSectionContent(index, 'items', newItems);
                          }}
                          placeholder="Question"
                          className="text-sm"
                        />
                        <Textarea
                          value={item.answer || ''}
                          onChange={(e) => {
                            const newItems = [...content.items];
                            newItems[itemIdx] = { ...item, answer: e.target.value };
                            updateSectionContent(index, 'items', newItems);
                          }}
                          placeholder="Réponse"
                          rows={2}
                          className="text-sm"
                        />
                      </>
                    )}
                    
                    {/* Standard items with title */}
                    {item.title !== undefined && item.question === undefined && (
                      <>
                        <Input
                          value={item.title || ''}
                          onChange={(e) => {
                            const newItems = [...content.items];
                            newItems[itemIdx] = { ...item, title: e.target.value };
                            updateSectionContent(index, 'items', newItems);
                          }}
                          placeholder="Titre"
                          className="text-sm"
                        />
                        {item.subtitle !== undefined && (
                          <Input
                            value={item.subtitle || ''}
                            onChange={(e) => {
                              const newItems = [...content.items];
                              newItems[itemIdx] = { ...item, subtitle: e.target.value };
                              updateSectionContent(index, 'items', newItems);
                            }}
                            placeholder="Sous-titre"
                            className="text-sm"
                          />
                        )}
                        {item.description !== undefined && (
                          <Textarea
                            value={item.description || ''}
                            onChange={(e) => {
                              const newItems = [...content.items];
                              newItems[itemIdx] = { ...item, description: e.target.value };
                              updateSectionContent(index, 'items', newItems);
                            }}
                            placeholder="Description"
                            rows={2}
                            className="text-sm"
                          />
                        )}
                        {item.image !== undefined && (
                          <div className="mt-1">
                            <ImageUploader
                              value={item.image || ''}
                              onChange={(url) => {
                                const newItems = [...content.items];
                                newItems[itemIdx] = { ...item, image: url };
                                updateSectionContent(index, 'items', newItems);
                              }}
                            />
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Name-based items (testimonials) */}
                    {item.name !== undefined && (
                      <>
                        <Input
                          value={item.name || ''}
                          onChange={(e) => {
                            const newItems = [...content.items];
                            newItems[itemIdx] = { ...item, name: e.target.value };
                            updateSectionContent(index, 'items', newItems);
                          }}
                          placeholder="Nom"
                          className="text-sm"
                        />
                        <Textarea
                          value={item.text || item.content || ''}
                          onChange={(e) => {
                            const newItems = [...content.items];
                            newItems[itemIdx] = { ...item, text: e.target.value };
                            updateSectionContent(index, 'items', newItems);
                          }}
                          placeholder="Témoignage"
                          rows={2}
                          className="text-sm"
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Section controls */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <Button
              size="sm"
              variant="ghost"
              disabled={index === 0}
              onClick={() => {
                const newSections = [...currentPage.sections];
                [newSections[index], newSections[index - 1]] = [newSections[index - 1], newSections[index]];
                setCurrentPage({ ...currentPage, sections: newSections });
              }}
            >
              <FiArrowUp className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={index === currentPage.sections.length - 1}
              onClick={() => {
                const newSections = [...currentPage.sections];
                [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
                setCurrentPage({ ...currentPage, sections: newSections });
              }}
            >
              <FiArrowDown className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={() => {
                const newSections = currentPage.sections.filter((_, i) => i !== index);
                setCurrentPage({ ...currentPage, sections: newSections });
              }}
            >
              <FiTrash2 className="h-3 w-3" />
            </Button>
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  };

  // Render presentation content editor
  const renderPresentationEditor = () => {
    const content = currentPage?.content || {};
    
    return (
      <Accordion type="multiple" defaultValue={['hero', 'presentation', 'faq', 'hotspots']} className="w-full">
        {/* Hero */}
        {content.hero && (
          <AccordionItem value="hero">
            <AccordionTrigger className="px-4 hover:bg-slate-50">
              <span className="flex items-center gap-2">
                <FiImage className="h-4 w-4 text-purple-600" />
                En-tête ({content.hero.title?.substring(0, 20)}...)
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-4">
              <div>
                <Label className="text-xs font-medium">Titre</Label>
                <Input
                  value={content.hero.title || ''}
                  onChange={(e) => updatePageContent('content.hero.title', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">Sous-titre</Label>
                <Textarea
                  value={content.hero.subtitle || ''}
                  onChange={(e) => updatePageContent('content.hero.subtitle', e.target.value)}
                  className="mt-1"
                  rows={2}
                />
              </div>
              {content.hero.backgroundImage !== undefined && (
                <div>
                  <Label className="text-xs font-medium">Image de fond</Label>
                  <ImageUploader
                    value={content.hero.backgroundImage || ''}
                    onChange={(url) => updatePageContent('content.hero.backgroundImage', url)}
                  />
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Presentation */}
        {content.presentation && (
          <AccordionItem value="presentation">
            <AccordionTrigger className="px-4 hover:bg-slate-50">
              <span className="flex items-center gap-2">
                <FiType className="h-4 w-4 text-blue-600" />
                Présentation
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-4">
              <div>
                <Label className="text-xs font-medium">Titre (HTML autorisé)</Label>
                <Textarea
                  value={content.presentation.title || ''}
                  onChange={(e) => updatePageContent('content.presentation.title', e.target.value)}
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div>
                <Label className="text-xs font-medium">Paragraphes</Label>
                <Textarea
                  value={content.presentation.paragraphs?.join('\n\n') || ''}
                  onChange={(e) => updatePageContent('content.presentation.paragraphs', e.target.value.split('\n\n').filter(p => p.trim()))}
                  className="mt-1"
                  rows={6}
                />
              </div>
              {content.presentation.photoboothImage !== undefined && (
                <div>
                  <Label className="text-xs font-medium">Image principale</Label>
                  <ImageUploader
                    value={content.presentation.photoboothImage || ''}
                    onChange={(url) => updatePageContent('content.presentation.photoboothImage', url)}
                  />
                </div>
              )}
              
              {/* Gallery */}
              {content.presentation.galleryImages && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium">Galerie ({content.presentation.galleryImages.length} images)</Label>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const current = content.presentation.galleryImages || [];
                        updatePageContent('content.presentation.galleryImages', [...current, '']);
                      }}
                    >
                      <FiPlus className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {content.presentation.galleryImages.map((img, idx) => {
                      const imgUrl = typeof img === 'string' ? img : img?.url || '';
                      return (
                        <div key={idx} className="relative">
                          <ImageUploader
                            value={imgUrl}
                            onChange={(url) => {
                              const newImages = [...content.presentation.galleryImages];
                              newImages[idx] = url;
                              updatePageContent('content.presentation.galleryImages', newImages);
                            }}
                          />
                          <button
                            onClick={() => {
                              const newImages = content.presentation.galleryImages.filter((_, i) => i !== idx);
                              updatePageContent('content.presentation.galleryImages', newImages);
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                          >
                            <FiX className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Hotspots */}
        {content.hotspots?.items && (
          <AccordionItem value="hotspots">
            <AccordionTrigger className="px-4 hover:bg-slate-50">
              <span className="flex items-center gap-2">
                <FiMove className="h-4 w-4 text-green-600" />
                Points interactifs ({content.hotspots.items.length})
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              {/* Image preview with hotspots */}
              {content.hotspots.mainImage && (
                <div className="space-y-2 mb-4">
                  <Label className="text-xs flex items-center gap-1 text-gray-500">
                    <FiMove className="h-3 w-3" />
                    Cliquez sur l'image pour repositionner le point sélectionné
                  </Label>
                  <div 
                    className="relative border rounded-lg overflow-hidden bg-gray-100 cursor-crosshair"
                    style={{ maxWidth: '100%' }}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
                      const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
                      const selectedIdx = content.hotspots._selectedIdx;
                      if (selectedIdx !== undefined && selectedIdx !== null && content.hotspots.items?.[selectedIdx]) {
                        const newItems = [...content.hotspots.items];
                        newItems[selectedIdx] = { ...newItems[selectedIdx], positionLeft: `${x}%`, positionTop: `${y}%` };
                        updatePageContent('content.hotspots.items', newItems);
                      }
                    }}
                  >
                    <img src={content.hotspots.mainImage} alt="Preview" className="w-full h-auto pointer-events-none" />
                    {content.hotspots.items.map((item, idx) => {
                      const isSelected = content.hotspots._selectedIdx === idx;
                      return (
                        <div
                          key={idx}
                          className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all ${isSelected ? 'z-20 scale-125' : 'z-10'}`}
                          style={{ left: item.positionLeft || '50%', top: item.positionTop || '50%' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const newSelectedIdx = isSelected ? null : idx;
                            updatePageContent('content.hotspots._selectedIdx', newSelectedIdx);
                          }}
                        >
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-full shadow-lg cursor-pointer text-xs ${
                            isSelected ? 'bg-amber-500 text-white ring-2 ring-amber-300' : 'bg-white text-gray-800 hover:bg-amber-100'
                          }`}>
                            <span className="font-medium truncate max-w-[60px]">{item.label || `#${idx + 1}`}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const newItems = [...content.hotspots.items, {
                    id: `hs-${Date.now()}`,
                    icon: 'camera',
                    label: 'Nouveau point',
                    tooltip: '',
                    positionLeft: '50%',
                    positionTop: '50%'
                  }];
                  updatePageContent('content.hotspots.items', newItems);
                }}
              >
                <FiPlus className="h-3 w-3 mr-1" />
                Ajouter un point
              </Button>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {content.hotspots.items.map((item, idx) => {
                  const isSelected = content.hotspots._selectedIdx === idx;
                  return (
                    <div 
                      key={idx} 
                      className={`p-2 rounded border space-y-2 cursor-pointer transition-all ${
                        isSelected ? 'bg-amber-50 border-amber-500 ring-1 ring-amber-200' : 'bg-slate-50 hover:border-slate-300'
                      }`}
                      onClick={() => updatePageContent('content.hotspots._selectedIdx', isSelected ? null : idx)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isSelected && <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />}
                          <span className="text-xs text-slate-500">Point {idx + 1}: {item.label || '(sans nom)'}</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newItems = content.hotspots.items.filter((_, i) => i !== idx);
                            updatePageContent('content.hotspots.items', newItems);
                          }}
                          className="text-red-500"
                        >
                          <FiTrash2 className="h-3 w-3" />
                        </button>
                      </div>
                      <div onClick={(e) => e.stopPropagation()}>
                        <Input
                          value={item.label || ''}
                          onChange={(e) => {
                            const newItems = [...content.hotspots.items];
                            newItems[idx] = { ...item, label: e.target.value };
                            updatePageContent('content.hotspots.items', newItems);
                          }}
                          placeholder="Label"
                          className="text-sm"
                        />
                      </div>
                      <div onClick={(e) => e.stopPropagation()}>
                        <Textarea
                          value={item.tooltip || ''}
                          onChange={(e) => {
                            const newItems = [...content.hotspots.items];
                            newItems[idx] = { ...item, tooltip: e.target.value };
                            updatePageContent('content.hotspots.items', newItems);
                          }}
                          placeholder="Description"
                          rows={2}
                          className="text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <span className="text-xs">X:</span>
                          <Slider
                            value={[parseInt(item.positionLeft) || 50]}
                            onValueChange={(v) => {
                              const newItems = [...content.hotspots.items];
                              newItems[idx] = { ...item, positionLeft: `${v[0]}%` };
                              updatePageContent('content.hotspots.items', newItems);
                            }}
                            min={0} max={100} step={1}
                            className="flex-1"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs">Y:</span>
                          <Slider
                            value={[parseInt(item.positionTop) || 50]}
                            onValueChange={(v) => {
                              const newItems = [...content.hotspots.items];
                              newItems[idx] = { ...item, positionTop: `${v[0]}%` };
                              updatePageContent('content.hotspots.items', newItems);
                            }}
                            min={0} max={100} step={1}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* FAQ */}
        {content.faq?.items && (
          <AccordionItem value="faq">
            <AccordionTrigger className="px-4 hover:bg-slate-50">
              <span className="flex items-center gap-2">
                <FiHelpCircle className="h-4 w-4 text-indigo-600" />
                FAQ ({content.faq.items.length} questions)
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const newItems = [...content.faq.items, { question: '', answer: '' }];
                  updatePageContent('content.faq.items', newItems);
                }}
              >
                <FiPlus className="h-3 w-3 mr-1" />
                Ajouter une question
              </Button>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {content.faq.items.map((item, idx) => (
                  <div key={idx} className="p-2 bg-slate-50 rounded border space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Q{idx + 1}</span>
                      <button
                        onClick={() => {
                          const newItems = content.faq.items.filter((_, i) => i !== idx);
                          updatePageContent('content.faq.items', newItems);
                        }}
                        className="text-red-500"
                      >
                        <FiTrash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <Input
                      value={item.question || ''}
                      onChange={(e) => {
                        const newItems = [...content.faq.items];
                        newItems[idx] = { ...item, question: e.target.value };
                        updatePageContent('content.faq.items', newItems);
                      }}
                      placeholder="Question"
                      className="text-sm"
                    />
                    <Textarea
                      value={item.answer || ''}
                      onChange={(e) => {
                        const newItems = [...content.faq.items];
                        newItems[idx] = { ...item, answer: e.target.value };
                        updatePageContent('content.faq.items', newItems);
                      }}
                      placeholder="Réponse"
                      rows={3}
                      className="text-sm"
                    />
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Reviews */}
        {content.reviews?.items && (
          <AccordionItem value="reviews">
            <AccordionTrigger className="px-4 hover:bg-slate-50">
              <span className="flex items-center gap-2">
                <FiStar className="h-4 w-4 text-yellow-600" />
                Avis ({content.reviews.items.length})
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const newItems = [...content.reviews.items, { name: '', text: '', rating: 5 }];
                  updatePageContent('content.reviews.items', newItems);
                }}
              >
                <FiPlus className="h-3 w-3 mr-1" />
                Ajouter un avis
              </Button>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {content.reviews.items.map((item, idx) => (
                  <div key={idx} className="p-2 bg-slate-50 rounded border space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Avis {idx + 1}</span>
                      <button
                        onClick={() => {
                          const newItems = content.reviews.items.filter((_, i) => i !== idx);
                          updatePageContent('content.reviews.items', newItems);
                        }}
                        className="text-red-500"
                      >
                        <FiTrash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <Input
                      value={item.name || ''}
                      onChange={(e) => {
                        const newItems = [...content.reviews.items];
                        newItems[idx] = { ...item, name: e.target.value };
                        updatePageContent('content.reviews.items', newItems);
                      }}
                      placeholder="Nom"
                      className="text-sm"
                    />
                    <Textarea
                      value={item.text || ''}
                      onChange={(e) => {
                        const newItems = [...content.reviews.items];
                        newItems[idx] = { ...item, text: e.target.value };
                        updatePageContent('content.reviews.items', newItems);
                      }}
                      placeholder="Témoignage"
                      rows={2}
                      className="text-sm"
                    />
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Models */}
        {content.models?.items && (
          <AccordionItem value="models">
            <AccordionTrigger className="px-4 hover:bg-slate-50">
              <span className="flex items-center gap-2">
                <FiGrid className="h-4 w-4 text-orange-600" />
                Modèles ({content.models.items.length})
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const newItems = [...content.models.items, { title: '', image: '', category: '' }];
                  updatePageContent('content.models.items', newItems);
                }}
              >
                <FiPlus className="h-3 w-3 mr-1" />
                Ajouter un modèle
              </Button>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {content.models.items.map((item, idx) => (
                  <div key={idx} className="p-2 bg-slate-50 rounded border space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Modèle {idx + 1}</span>
                      <button
                        onClick={() => {
                          const newItems = content.models.items.filter((_, i) => i !== idx);
                          updatePageContent('content.models.items', newItems);
                        }}
                        className="text-red-500"
                      >
                        <FiTrash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <Input
                      value={item.title || ''}
                      onChange={(e) => {
                        const newItems = [...content.models.items];
                        newItems[idx] = { ...item, title: e.target.value };
                        updatePageContent('content.models.items', newItems);
                      }}
                      placeholder="Titre"
                      className="text-sm"
                    />
                    <ImageUploader
                      value={item.image || ''}
                      onChange={(url) => {
                        const newItems = [...content.models.items];
                        newItems[idx] = { ...item, image: url };
                        updatePageContent('content.models.items', newItems);
                      }}
                    />
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-100">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-bold text-slate-800">Éditeur de Pages</h1>
          {currentPage && (
            <Badge variant="outline" className="font-medium">
              {currentPage.name}
            </Badge>
          )}
          {hasUnsavedChanges && (
            <span className="flex items-center gap-1 text-xs text-amber-600">
              <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
              Modifications non sauvegardées
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setPreviewMode('desktop')}
              className={`p-2 rounded ${previewMode === 'desktop' ? 'bg-white shadow-sm' : 'hover:bg-slate-200'}`}
            >
              <FiMonitor className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPreviewMode('mobile')}
              className={`p-2 rounded ${previewMode === 'mobile' ? 'bg-white shadow-sm' : 'hover:bg-slate-200'}`}
            >
              <FiSmartphone className="h-4 w-4" />
            </button>
          </div>
          
          <Button variant="outline" size="sm" onClick={() => setIframeKey(k => k + 1)}>
            <FiRefreshCw className="h-4 w-4" />
          </Button>
          
          <Button variant="outline" size="sm" onClick={() => window.open(`/${currentPage?.slug || ''}`, '_blank')}>
            <FiExternalLink className="h-4 w-4 mr-2" />
            Voir
          </Button>
          
          <Button 
            onClick={handleSave} 
            disabled={saving || !hasUnsavedChanges} 
            className={hasUnsavedChanges ? "bg-amber-600 hover:bg-amber-700" : "bg-slate-400"}
          >
            <FiSave className="h-4 w-4 mr-2" />
            {saving ? 'Sauvegarde...' : hasUnsavedChanges ? 'Enregistrer' : 'Sauvegardé'}
          </Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Left - Pages List */}
        <div className="w-[220px] bg-white border-r flex flex-col shrink-0">
          <div className="p-3 border-b">
            <h2 className="font-semibold text-sm text-slate-700">Pages</h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            {Object.entries(groupedPages).map(([group, groupPages]) => {
              const isExpanded = expandedGroups[group] !== false;
              return (
                <div key={group} className="border-b">
                  <button
                    onClick={() => setExpandedGroups(p => ({ ...p, [group]: !p[group] }))}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-slate-50 text-xs font-medium text-slate-600"
                  >
                    {group}
                    {isExpanded ? <FiChevronDown className="h-3 w-3" /> : <FiChevronRight className="h-3 w-3" />}
                  </button>
                  {isExpanded && groupPages.map(page => (
                    <button
                      key={page.slug}
                      onClick={() => navigate(`/dashboard/pages/${page.slug}`)}
                      className={`w-full px-4 py-1.5 text-left text-xs flex items-center gap-2 ${
                        currentPage?.slug === page.slug 
                          ? 'bg-amber-50 text-amber-700 border-l-2 border-amber-500' 
                          : 'hover:bg-slate-50 text-slate-600'
                      }`}
                    >
                      <FiFile className="h-3 w-3" />
                      <span className="truncate">{page.name}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Center - Preview */}
        <div className="flex-1 bg-slate-200 p-4 overflow-hidden flex flex-col">
          <div className={`flex-1 bg-white shadow-xl overflow-hidden rounded-lg flex items-center justify-center ${
            previewMode === 'mobile' ? 'p-4' : ''
          }`}>
            <div className={`h-full transition-all ${
              previewMode === 'mobile' ? 'w-[375px] rounded-3xl border-8 border-slate-800' : 'w-full'
            }`}>
              <iframe
                ref={iframeRef}
                key={iframeKey}
                src={`/${currentPage?.slug || ''}`}
                className="w-full h-full border-0"
                title="Aperçu"
              />
            </div>
          </div>
        </div>

        {/* Right - Editor */}
        <div className="w-[380px] bg-white border-l flex flex-col shrink-0 overflow-hidden">
          {currentPage ? (
            <>
              <div className="p-3 border-b bg-slate-50">
                <h3 className="font-semibold text-sm text-slate-800">
                  Modifier: {currentPage.name}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  {isPresentationPage ? 'Page de présentation' : `${currentPage.sections?.length || 0} sections`}
                </p>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {isPresentationPage ? (
                  renderPresentationEditor()
                ) : hasSections ? (
                  <Accordion type="multiple" defaultValue={currentPage.sections?.slice(0, 3).map((_, i) => `section-${i}`)} className="w-full">
                    {currentPage.sections.map((section, idx) => renderSectionEditor(section, idx))}
                  </Accordion>
                ) : (
                  <div className="p-8 text-center text-slate-500">
                    <FiSettings className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p className="text-sm">Cette page n'a pas de contenu éditable</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center text-slate-500">
                <FiFile className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>Sélectionnez une page</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
